import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen" data-testid="terms-page">
      <header className="border-b-2" style={{ borderColor: "var(--cs-ink)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={26} /></Link>
          <Link to="/" className="brut-btn brut-btn-ghost text-xs"><ArrowLeft size={14} /> Back</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-display text-5xl font-black tracking-tighter">Terms of Service</h1>
        <p className="text-[var(--cs-ink-2)] mt-2 font-semibold">Last updated: {new Date().toLocaleDateString()}</p>

        <Section title="1. The CashStreak service">
          <p>CashStreak is an online platform that lets users in supported cities ("Posters") publish short paid tasks and other users ("Taskers") claim and complete them in exchange for cash. CashStreak is a venue — it is not a party to the contract between Posters and Taskers.</p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least 18 years old and legally able to enter into a binding contract. You must provide accurate information and keep your account secure.</p>
        </Section>

        <Section title="3. Payments &amp; fees">
          <ul className="list-disc pl-6 space-y-1">
            <li>Posters pay the full task price upfront via Stripe. Funds are held in escrow on the platform until the task is marked complete.</li>
            <li>Upon completion, Taskers receive the task price minus a 12% platform fee. First-time Taskers may have the fee waived as a one-time promo.</li>
            <li>If a task is cancelled before being claimed, the Poster is refunded automatically. Refunds for claimed-but-uncompleted tasks are handled case-by-case via support.</li>
            <li>Tasker payouts happen via Stripe Connect Express to the linked bank account, typically within 1–2 business days.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable use">
          <p>You may not use CashStreak to post or claim tasks that are illegal, dangerous, abusive, sexual, or that violate any third party's rights. Examples of prohibited tasks include drug delivery, weapons handling, anything requiring a license you don't have, or anything that would put either party at risk. We reserve the right to remove tasks, refund payments, and ban accounts that violate these rules.</p>
        </Section>

        <Section title="5. Safety">
          <p>You alone are responsible for meeting and interacting with other users safely. Trust your instincts, meet in public when possible, and report bad actors immediately. CashStreak does not perform background checks (yet) and is not responsible for the actions of users on the platform.</p>
        </Section>

        <Section title="6. Disputes between users">
          <p>If a task isn't completed as agreed, contact the other party first through in-app chat. If you can't resolve it, contact support@mycashstreak.com. CashStreak may, at its sole discretion, refund the Poster, reverse a payout, or suspend the offending account.</p>
        </Section>

        <Section title="7. Account suspension &amp; termination">
          <p>We may suspend or terminate accounts that violate these terms, defraud users, or harm the community. You may delete your account at any time by contacting support@mycashstreak.com.</p>
        </Section>

        <Section title="8. Disclaimer of warranties">
          <p>CashStreak is provided "as is". We do not guarantee that tasks will be claimed, completed, or completed to your satisfaction. We make no warranties about the conduct or quality of any user.</p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>To the maximum extent permitted by law, CashStreak's total liability arising from your use of the service is limited to the platform fees you paid us in the 12 months preceding the claim, or $100, whichever is greater.</p>
        </Section>

        <Section title="10. Changes">
          <p>We may update these terms over time. Material changes will be announced in-app at least 7 days before taking effect. Continued use of the service after changes constitutes acceptance.</p>
        </Section>

        <Section title="11. Governing law">
          <p>These terms are governed by the laws of the State of Tennessee, USA, without regard to its conflict-of-law principles.</p>
        </Section>

        <Section title="12. Contact">
          <p>Questions or feedback? Email <a className="underline font-bold" href="mailto:support@mycashstreak.com">support@mycashstreak.com</a>.</p>
        </Section>
      </main>
      <footer className="py-8 text-center text-xs text-[var(--cs-ink-2)]">© {new Date().getFullYear()} CashStreak</footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">{title}</h2>
      <div className="mt-3 text-[var(--cs-ink)] leading-relaxed">{children}</div>
    </section>
  );
}
